import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import {
  Button,
  EmptyState,
  buttonClasses,
  failedEmptyState,
  notFoundEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import { ROUTES, enquiryEditPath } from "../../../routes/paths";
import { decodeRouteId } from "../../../routes/routeIds";
import EnquiryDetailSkeleton from "../components/EnquiryDetailSkeleton";
import EnquiryDetailsView from "../components/EnquiryDetailsView";
import PaymentRequestedModal from "../components/PaymentRequestedModal";
import RequestPaymentModal from "../components/RequestPaymentModal";
// The "money is in" question, asked the same way here and on a client's
// payment history — see `ConfirmPaymentModal` — and the other answer beside it.
import ConfirmPaymentModal from "../../payments/components/ConfirmPaymentModal";
import DeclinePaymentModal from "../../payments/components/DeclinePaymentModal";
// A deep import rather than the billing barrel: that barrel pulls in the
// checkout and result screens, and this page only needs to know whether the
// workspace is allowed to change anything.
import { useSubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
import { toEnquiryDetail } from "../enquiryMapping";
import { useEnquiry } from "../hooks/useEnquiry";
import { markEnquiryOpened } from "../openedEnquiries";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
// Straight from the plan. The payment calls go to the backend — see
// `enquiriesService` for what each one patches.
import {
  cancelEnquiryPayment,
  clearEnquiryStatusOverride,
  confirmEnquiryPayment,
  requestEnquiryPayment,
} from "../services/enquiriesService";
import { ApiError } from "../../../lib/http";
import type {
  EnquiryPayment,
  NewEnquiryPayment,
} from "../types/enquiries.types";

const EnquiryDetailPage: React.FC = () => {
  // Enciphered in the path — see `routeIds`. A token that does not decode is
  // "", which finds no enquiry and lands on the "Enquiry not found" screen
  // below.
  const { enquiryId: token = "" } = useParams();
  const enquiryId = decodeRouteId(token);

  const {
    enquiry: record,
    status: loadStatus,
    reload,
    refresh,
  } = useEnquiry(enquiryId);
  const navigate = useNavigate();
  const toast = useToast();
  const getToken = useTokenGetter();

  // Read before the returns below, since a hook cannot sit behind one.
  const phase = useLoadPhase(loadStatus === "loading");

  /**
   * Opening this screen is what takes the enquiry's "New" tag off the list and
   * one off the rail's count — see `openedEnquiries`.
   *
   * Only once the record is actually in hand: a read still on its way, one
   * that failed, and a `public_id` that is not an enquiry at all are none of
   * them somebody having read it, and marking on arrival at the URL would
   * clear the tag for a screen that went on to say "Enquiry not found".
   *
   * Above the returns below, since a hook cannot sit behind one. A no-op for
   * an enquiry already opened, so nothing is published on a second visit.
   */
  useEffect(() => {
    if (loadStatus === "ready") markEnquiryOpened(enquiryId);
  }, [loadStatus, enquiryId]);

  // Enquiries are one of the three things a subscription is what pays for, so
  // with none running everything this screen writes is off — the record itself
  // stays readable, the way the list it was opened from does. Read here and
  // handed down, so the whole screen gives one answer. See
  // `useSubscriptionGate`.
  const gate = useSubscriptionGate();

  const [requesting, setRequesting] = useState(false);
  // Set while the raise is with the server, so the dialog's Confirm spins
  // rather than taking a second press and raising the request twice.
  const [sending, setSending] = useState(false);
  // The same, for the "money is in" answer being given below.
  const [settling, setSettling] = useState(false);
  // The payment that has just gone out, which is the whole of what the sent
  // dialog is open for. Read off the store's copy rather than the form's, so
  // the date and the amount on screen are the ones that were saved.
  const [sent, setSent] = useState<EnquiryPayment | null>(null);
  // Whether the "money is in" question is on screen. Marking a payment
  // received is not something to do by mis-clicking a button beside an amount.
  const [confirming, setConfirming] = useState(false);
  // The same, for the other answer. Calling a request off cannot be taken
  // back either, and the button that does it sits beside the one that confirms.
  const [declining, setDeclining] = useState(false);

  const enquiry = useMemo(
    () => (record ? toEnquiryDetail(record) : undefined),
    [record]
  );

  // The details drawn as blocks while the record is fetched, so the enquiry
  // lands into a page that is already the shape it will keep — but only once
  // the fetch has visibly stalled. One that arrives quickly opens on itself,
  // with no screenful of blocks in between.
  //
  // Ahead of the not-found screen below, which is about a record that came
  // back missing rather than one still on its way.
  if (loadStatus === "loading") return <EnquiryDetailSkeleton phase={phase} />;

  // A request that never came back is not an enquiry that is not there: this
  // one is worth trying again, and says so rather than telling the user their
  // enquiry has gone.
  if (loadStatus === "failed") {
    return (
      <div className="min-h-full md:h-full flex flex-col md:overflow-hidden p-page pt-page-top">
        <PageHeader
          title="Enquiry"
          breadcrumbs={[
            { label: "Enquiries", to: ROUTES.enquiries },
            { label: "Enquiry" },
          ]}
        />
        <div className="mt-5 flex-1 min-h-0 flex items-center justify-center card-surface border border-line bg-white">
          <EmptyState
            {...failedEmptyState(
              "We could not load this enquiry",
              <Button variant="outline" size="sm" onClick={reload}>
                Try again
              </Button>
            )}
          />
        </div>
      </div>
    );
  }

  if (!enquiry) {
    return (
      <div className="min-h-full md:h-full flex flex-col md:overflow-hidden p-page pt-page-top">
        <PageHeader
          title="Enquiry not found"
          breadcrumbs={[
            { label: "Enquiries", to: ROUTES.enquiries },
            { label: "Enquiry not found" },
          ]}
        />
        <div className="mt-5 flex-1 min-h-0 flex items-center justify-center card-surface border border-line bg-white">
          <EmptyState
            {...notFoundEmptyState(
              "That enquiry no longer exists",
              "It may have been deleted since this link was opened. Every other enquiry is unaffected.",
              // A link and not a button — this goes somewhere. See the note
              // on `buttonClasses`.
              <Link
                to={ROUTES.enquiries}
                className={buttonClasses({ variant: "secondary", size: "sm" })}
              >
                Back to enquiries
              </Link>
            )}
          />
        </div>
      </div>
    );
  }

  /**
   * Confirmed on the dialog's second step, so the request goes to the backend
   * and the form makes way for what it came back as. No toast on the way
   * through: the dialog that replaces it says the same thing, at greater
   * length and with somewhere to go next.
   *
   * A request that could not be raised is the one case that does toast — and
   * leaves the form open, holding what was picked, rather than closing over a
   * failure.
   */
  const handleConfirmRequest = async (payment: NewEnquiryPayment) => {
    if (sending) return;
    setSending(true);

    try {
      const saved = await requestEnquiryPayment(getToken, enquiry.id, payment);
      setRequesting(false);
      setSent(saved);
    } catch (cause) {
      toast.error(
        "Could not request this payment",
        cause instanceof ApiError
          ? cause.message
          : "We could not reach the server. Check your connection and try again."
      );
      console.error("enquiry payment request failed:", cause);
    } finally {
      setSending(false);
    }
  };

  /**
   * Reads the enquiry again for the stage beside its name, after an answer
   * that may have moved it.
   *
   * Settling a request is the backend's business as much as this screen's: the
   * confirm and the cancel say nothing about a stage, but an enquiry that has
   * paid is not at the same point in the pipeline as one that has been asked,
   * and only the server knows where it put it. So the pill is not guessed at
   * here — there is no fifth stage on this side to guess with — it is re-read.
   *
   * The "Payment sent" this browser wrote when the request went out is dropped
   * first, or it would sit on top of the answer and hide the move.
   *
   * Quietly, and that is the point: the money has just been marked in, and a
   * page that blanked to a skeleton over a stage nobody was waiting on would
   * take the confirmation off the screen to fetch a word. Everything else on
   * the page is already right; this is only the pill catching up.
   */
  const refreshStage = () => {
    clearEnquiryStatusOverride(enquiry.id);
    refresh();
  };

  /**
   * Marks the money in, on the day the dialog was told it arrived. The request
   * is settled through its own id — the same call a client's payment history
   * makes — so the dialog stays up until the server has it.
   *
   * The payment goes down with it: it is the one on screen, whether it came back
   * with the enquiry or was raised a moment ago on this page.
   */
  const handleConfirmPayment = async (receivedOn: string) => {
    if (settling || !enquiry.payment) return;
    setSettling(true);

    try {
      await confirmEnquiryPayment(
        getToken,
        enquiry.id,
        enquiry.payment,
        receivedOn
      );
      setConfirming(false);
      refreshStage();
      toast.success(
        "Payment confirmed",
        `${enquiry.name}'s payment has been marked as received.`
      );
    } catch (cause) {
      toast.error(
        "Could not confirm this payment",
        cause instanceof ApiError
          ? cause.message
          : "We could not reach the server. Check your connection and try again."
      );
      console.error("enquiry payment confirmation failed:", cause);
    } finally {
      setSettling(false);
    }
  };

  /**
   * Calls the request off, through the same id the confirm settles. The dialog
   * stays up until the server has it, so a cancellation that could not be made
   * leaves the question on screen to answer again rather than closing over a
   * failure — the way the client's payment history does it.
   */
  const handleDeclinePayment = async () => {
    if (settling || !enquiry.payment) return;
    setSettling(true);

    try {
      await cancelEnquiryPayment(getToken, enquiry.id, enquiry.payment);
      setDeclining(false);
      refreshStage();
      toast.success(
        "Payment declined",
        `The payment requested from ${enquiry.name} has been called off.`
      );
    } catch (cause) {
      toast.error(
        "Could not decline this payment",
        cause instanceof ApiError
          ? cause.message
          : "We could not reach the server. Check your connection and try again."
      );
      console.error("enquiry payment cancellation failed:", cause);
    } finally {
      setSettling(false);
    }
  };

  return (
    <div className="min-h-full md:h-full flex flex-col md:overflow-hidden p-page pt-page-top max-md:pb-0">
      <PageHeader
        title="New enquiry"
        breadcrumbs={[
          { label: "Enquiries", to: ROUTES.enquiries },
          { label: enquiry.name },
        ]}
      />

      <div className="mt-5 flex-1 min-h-0 overflow-y-auto custom-scrollbar-overlay max-md:pb-4">
        <EnquiryDetailsView
          enquiry={enquiry}
          gate={gate}
          onRequestPayment={() => setRequesting(true)}
          onConfirmPayment={() => setConfirming(true)}
          onDeclinePayment={() => setDeclining(true)}
          onEdit={() => navigate(enquiryEditPath(enquiry.id))}
        />
      </div>

      {requesting && (
        <RequestPaymentModal
          recipient={`${enquiry.salutation} ${enquiry.name}`.trim()}
          // A blank email is a dash by the time the detail record is built.
          email={enquiry.email === "-" ? "" : enquiry.email}
          busy={sending}
          // Not while it is with the server: the request is going out either
          // way, and a dialog closed over it would leave nothing to say so.
          onClose={() => !sending && setRequesting(false)}
          onConfirm={handleConfirmRequest}
        />
      )}

      {sent && (
        <PaymentRequestedModal
          payment={sent}
          recipient={`${enquiry.salutation} ${enquiry.name}`.trim()}
          email={enquiry.email === "-" ? "" : enquiry.email}
          // Stays put: the request is a row on the page behind the dialog,
          // so getting out of the way is the whole of showing it.
          onViewDetails={() => setSent(null)}
          onGoToEnquiries={() => navigate(ROUTES.enquiries)}
        />
      )}

      {/* Asked before the pill turns green, because nothing takes it back:
          the money is either in or it is not, and this screen is the only
          thing that says which — down to the day it came in, which is what
          the dialog asks for. The same one the client's payment history uses,
          so the question reads identically wherever it is answered. */}
      {confirming && enquiry.payment && (
        <ConfirmPaymentModal
          amount={enquiry.payment.amount}
          payer={enquiry.name}
          planName={enquiry.payment.planName}
          duration={enquiry.payment.duration}
          requestedAt={enquiry.payment.requestedAt}
          busy={settling}
          onClose={() => setConfirming(false)}
          onConfirm={handleConfirmPayment}
        />
      )}

      {/* The other answer, asked the same way a client's payment history asks
          it — a plain question with the amount in it, since a request called
          off stays called off. */}
      {declining && enquiry.payment && (
        <DeclinePaymentModal
          amount={enquiry.payment.amount}
          payer={enquiry.name}
          planName={enquiry.payment.planName}
          duration={enquiry.payment.duration}
          busy={settling}
          onClose={() => setDeclining(false)}
          onConfirm={handleDeclinePayment}
        />
      )}
    </div>
  );
};

export default EnquiryDetailPage;
